import { Injectable } from '@angular/core';
import {
  HttpContextToken,
  HttpClient,
  HttpErrorResponse,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { StorageService } from './services/storage.service';
import { Config } from './config';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { TokenRefresh } from './interfaces/auth/token-refresh.interface';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppInjector } from './services/app-injector.service';

const REFRESH_RETRIED = new HttpContextToken<boolean>(() => false);

type TokenRefreshOutcome =
  | { status: 'idle' }
  | { status: 'refreshing' }
  | { status: 'success'; token: string }
  | { status: 'error'; error: unknown };

@Injectable()
export class APIInterceptor implements HttpInterceptor {
  refreshInProgress: boolean = false;
  showSessionEndMessage: boolean = true;
  messageCounter: number = 0;
  tokenTurnSubject: BehaviorSubject<TokenRefreshOutcome> = new BehaviorSubject<TokenRefreshOutcome>({
    status: 'idle',
  });
  translate: TranslateService;
  private sessionGeneration = 0;

  constructor(
    public storage: StorageService,
    public router: Router,
    public route: ActivatedRoute,
    public http: HttpClient,
    public toast: ToastrService
  ) {
    const injector = AppInjector.getInjector();
    this.translate = injector.get(TranslateService);
    this.storage.sessionEnded$.subscribe(() => {
      this.sessionGeneration++;
      this.cancelRefresh(new Error('Authentication session ended.'));
    });
  }

  correctNavigationLang() {
    let res = navigator.language;
    const resSplitFloor = res.split('_');
    const resSplitDash = res.split('-');

    if (resSplitFloor.length > 1) {
      res = resSplitFloor[0];
    }
    if (resSplitDash.length > 1) {
      res = resSplitDash[0];
    }
    res = this.translate.getLangs().indexOf(res) > -1 ? res : 'en';
    return res.toLowerCase();
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    // change url on server  url + add token if exist
    let formatUrl = { url: req.url };
    const paths = ['/project/create', '/help-ukraine-request/create'];

    if (
      !this.isRefreshRequest(req) &&
      ((this.storage.get(StorageService.SESSION_COOKIE) &&
        Config.URL_WITHOUT_AUTHORIZATION.indexOf(req.url.split('/api/')[1]) === -1) ||
        paths.indexOf(window.location.pathname) > -1)
    ) {
      formatUrl = Object.assign(formatUrl, {
        setHeaders: {
          Authorization: 'Bearer ' + this.storage.get(StorageService.SESSION_COOKIE),
          'Accept-Language': this.storage.get('lang'),
        },
      });
    } else {
      let lang = this.storage.get('lang')
        ? this.storage.get('lang')
        : this.correctNavigationLang().toLowerCase();
      formatUrl = Object.assign(formatUrl, { setHeaders: { 'Accept-Language': lang } });
    }
    req = req.clone(formatUrl);

    return next.handle(req).pipe(
      catchError((err: HttpErrorResponse) => {
        if (this.isRefreshRequest(req)) {
          this.failRefresh(err);
          return throwError(() => err);
        }

        switch (err?.error?.message) {
          case 'jwt_token_not_found': {
            this.storage.logout();
            if (this.showSessionEndMessage) {
              this.toast.error(
                this.translate.instant('AUTH.session_out'),
                this.translate.instant('AUTH.session_out_title')
              );
              this.showSessionEndMessage = false;
            }
            return throwError(() => err);
          }
          case 'expired_jwt_token': {
            if (req.context.get(REFRESH_RETRIED)) {
              this.storage.logout(false);
              return throwError(() => err);
            }

            const currentToken = this.storage.get(StorageService.SESSION_COOKIE);
            const requestToken = req.headers.get('Authorization')?.replace(/^Bearer\s+/i, '');
            if (currentToken && requestToken && currentToken !== requestToken) {
              return this.retryWithToken(req, next, currentToken);
            }

            return this.refreshInProgress
              ? this.tokenTurnContinue(req, next)
              : this.refreshToken(req, next);
          }
          case 'refresh_token_expired': {
            this.storage.logout();
            if (this.showSessionEndMessage) {
              this.toast.error(
                this.translate.instant('AUTH.session_out'),
                this.translate.instant('AUTH.session_out_title')
              );
              this.showSessionEndMessage = false;
            }
            return throwError(() => err);
          }
          case 'Invalid JWT Token': {
            this.storage.logout();
            if (this.showSessionEndMessage) {
              this.toast.error(
                this.translate.instant('AUTH.session_out'),
                this.translate.instant('AUTH.session_out_title')
              );
              this.showSessionEndMessage = false;
            }
            return throwError(() => err);
          }
          case 'account_is_blocked_by_administration': {
            this.storage.logout(false);
            this.storage.put('account_blocked', 'true');
            this.toast.error(
              this.translate.instant('AUTH.account_is_blocked_by_administration'),
              this.translate.instant('AUTH.session_out_title')
            );
            return throwError(() => err);
          }
          case 'unauthorized': {
            this.storage.logout(false);
            const currentParams = this.route.snapshot.queryParams;
            this.router.navigate([], { queryParams: { ...currentParams, auth: 'login' } });
            // this.toast.error(this.translate.instant('AUTH.session_out'), this.translate.instant('AUTH.session_out_title'));
            return throwError(() => err);
          }
          default: {
            this.notifyServerError(err);
            return throwError(() => err);
          }
        }
      })
    );
  }

  get countSimilarErrors() {
    return this.messageCounter++;
  }

  private refreshToken(req: HttpRequest<any>, next: HttpHandler) {
    this.refreshInProgress = true;
    this.tokenTurnSubject.next({ status: 'refreshing' });
    const generation = this.sessionGeneration;
    const refreshToken = this.storage.get(StorageService.SESSION_REFRESH_COOKIE);

    if (!refreshToken) {
      const error = new Error('Refresh token is not available.');
      this.failRefresh(error);
      return throwError(() => error);
    }

    return this.http
      .post(Config.API_URL + '/token/refresh', {
        refresh_token: refreshToken,
      })
      .pipe(
        switchMap((data: TokenRefresh) => {
          if (
            !this.refreshInProgress ||
            generation !== this.sessionGeneration ||
            this.storage.get(StorageService.SESSION_REFRESH_COOKIE) !== refreshToken
          ) {
            return throwError(() => new Error('Authentication session ended.'));
          }
          if (!data?.token || !data?.refresh_token) {
            return throwError(() => new Error('Invalid token refresh response.'));
          }

          this.storage.setToken(data); // have token
          this.refreshInProgress = false;
          this.tokenTurnSubject.next({ status: 'success', token: data.token });
          this.storage.refreshUserGroups();
          return this.retryWithToken(req, next, data.token);
        }),
        catchError((error: unknown) => {
          this.failRefresh(error);
          return throwError(() => error);
        })
      );
  }

  private tokenTurnContinue(req: HttpRequest<any>, next: HttpHandler) {
    return this.tokenTurnSubject
      .pipe(
        filter((outcome) => outcome.status === 'success' || outcome.status === 'error'),
        take(1),
        switchMap((outcome) => {
          if (outcome.status === 'error') {
            return throwError(() => outcome.error);
          }
          if (outcome.status !== 'success') {
            return throwError(() => new Error('Token refresh did not settle successfully.'));
          }
          return this.retryWithToken(req, next, outcome.token);
        })
      );
  }

  private retryWithToken(req: HttpRequest<any>, next: HttpHandler, token: string) {
    return next.handle(
      req.clone({
        context: req.context.set(REFRESH_RETRIED, true),
        setHeaders: { Authorization: 'Bearer ' + token },
      })
    );
  }

  private isRefreshRequest(req: HttpRequest<any>): boolean {
    return req.url === Config.API_URL + '/token/refresh';
  }

  private failRefresh(error: unknown): void {
    if (!this.refreshInProgress) {
      return;
    }
    this.refreshInProgress = false;
    this.tokenTurnSubject.next({ status: 'error', error });
    this.storage.logout();

    const httpError = error as HttpErrorResponse;
    if (httpError?.status === 500) {
      this.notifyServerError(httpError);
    }
    if (
      (error as HttpErrorResponse)?.error?.message === 'refresh_token_expired' &&
      this.showSessionEndMessage
    ) {
      this.toast.error(
        this.translate.instant('AUTH.session_out'),
        this.translate.instant('AUTH.session_out_title')
      );
      this.showSessionEndMessage = false;
    }
  }

  private cancelRefresh(error: unknown): void {
    if (this.refreshInProgress) {
      this.refreshInProgress = false;
      this.tokenTurnSubject.next({ status: 'error', error });
    }
  }

  private notifyServerError(error: HttpErrorResponse): void {
    if (error.status === 500) {
      this.toast.error(
        this.translate.instant('AUTH.server_problem'),
        this.translate.instant('AUTH.unexpected_error')
      );
    }
  }
}
